const ANNOUNCEMENT_KEY = 'video-chat.announcement.new-chat-message';

function buildMessageKey(message) {
  if (!message) {
    return null;
  }
  const authorUid = message.author?.uid ?? '';
  const text = message.content?.text ?? '';
  const date = message.date ?? '';
  return `${authorUid}|${date}|${text}`;
}

function focusComposer(rootElement) {
  const textarea = rootElement?.querySelector('.chat-form textarea');
  if (textarea && typeof textarea.focus === 'function') {
    textarea.focus();
  }
}

/**
 * Watches the chat message collection and, for every *new incoming* message
 * (i.e. added by someone other than the current user, not loaded as history):
 *   - announces "New chat message, <text>" through liveAnnouncer
 *   - moves focus into the composer textarea so the user can reply
 *
 * Pure helper — all Angular interop (`$scope.$watchCollection`, `$timeout`)
 * is injected, which makes it trivial to unit-test in jsdom without
 * angular-mocks.
 */
export function installIncomingMessageAnnouncements({
  $element,
  $scope,
  $timeout,
  liveAnnouncer,
  $translate,
  getMessages,
  getCurrentUserId,
}) {
  const root = $element?.[0] ?? null;
  let lastAnnouncedMessageKey = null;

  $scope.$watchCollection(
    () => {
      const messages = typeof getMessages === 'function' ? getMessages() : [];
      return Array.isArray(messages) ? messages : [];
    },
    (messages, previous) => {
      if (!Array.isArray(messages) || messages.length === 0) {
        return;
      }
      // Skip the initial render and any non-growth change — those are loaded
      // history or list reshuffles, not real-time incoming messages. We still
      // seed the dedupe key so a follow-up additive change with the same
      // tail message isn't announced twice.
      if (!Array.isArray(previous) || messages.length <= previous.length) {
        lastAnnouncedMessageKey = buildMessageKey(
          messages[messages.length - 1]
        );
        return;
      }

      const latest = messages[messages.length - 1];
      if (!latest) {
        return;
      }
      const key = buildMessageKey(latest);
      if (key === lastAnnouncedMessageKey) {
        return;
      }
      lastAnnouncedMessageKey = key;

      const ownUserId =
        typeof getCurrentUserId === 'function' ? getCurrentUserId() : null;
      const isOutgoing =
        ownUserId != null && latest.author?.uid === ownUserId;
      if (isOutgoing) {
        return;
      }

      if (liveAnnouncer && $translate) {
        const text = latest.content?.text ?? '';
        liveAnnouncer.announce(
          $translate.instant(ANNOUNCEMENT_KEY, { text }),
          'polite'
        );
      }
      // Defer one digest so the composer is in the DOM (it's `ng-if`-gated
      // on the chat being available).
      $timeout(() => focusComposer(root));
    }
  );
}
